import { Currency, Router, RouterVersion, Trade, TradeType } from '@nominex/nomiswap-sdk'
import { useMemo } from 'react'
import { type Address, parseEther as viemParseEther } from 'viem'
import { useWriteContract } from 'wagmi'
import { INITIAL_ALLOWED_SLIPPAGE } from 'config/constants'
import { useWagmi } from 'libs/wagmi'
import { USER_DENIED_SIGNATURE } from 'libs/wagmi/errors'
import { useTransactionAdder } from 'state/transactions/hooks'
import { basisPointsToPercent, isAddress } from 'utils'
import { ROUTERS_MAP } from '@nominex/nomiswap-sdk'
import truncateHash from 'utils/truncateHash'
import useTransactionDeadline from './useTransactionDeadline'
import INomiswapRouter04 from 'config/abi/INomiswapRouter04.json'

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID,
}

// Helper to convert SDK value to bigint for viem
function ensureBigInt(value: any): bigint | undefined {
  if (!value) return undefined

  if (typeof value === 'bigint') return value

  if (typeof value === 'string') {
    // Hex strings
    if (value.startsWith('0x')) {
      return BigInt(value)
    }
    // Decimal strings like "0.1" need parseEther
    if (value.includes('.')) {
      return viemParseEther(value)
    }
    // Whole number strings
    return BigInt(value)
  }

  if (typeof value === 'number') {
    if (value % 1 !== 0) {
      return viemParseEther(value.toString())
    }
    return BigInt(value)
  }

  return undefined
}

// Helper to check if value is zero (hex)
function isZero(hexValue: string): boolean {
  return /^0x0*$/.test(hexValue)
}

/**
 * Returns a function that will execute a swap using wagmi/viem
 * Migrated from ethers to wagmi for better compatibility with Wagmi v2
 */
export function useSwapCallback(
  trade: Trade<Currency, Currency, TradeType> | undefined,
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE,
  recipientAddress: string | null,
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: string | null } {
  const { account, chainId } = useWagmi()
  const deadline = useTransactionDeadline()
  const addTransaction = useTransactionAdder()
  const { writeContractAsync } = useWriteContract()

  const recipient = (recipientAddress ?? account) as Address

  // Get swap parameters from SDK
  const swapParams = useMemo(() => {
    if (!trade || !account || !chainId || !recipient || !deadline) {
      return null
    }

    const params = Router.swapCallParameters(trade, {
      feeOnTransfer: false,
      allowedSlippage: basisPointsToPercent(allowedSlippage),
      recipient,
      deadline,
    })

    return params
  }, [trade, account, chainId, recipient, deadline, allowedSlippage])

  // Get router address
  const routerAddress = useMemo(() => {
    if (!chainId) return null
    return ROUTERS_MAP[chainId]?.[RouterVersion.V3] as Address
  }, [chainId])

  return useMemo(() => {
    if (!trade || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Missing dependencies' }
    }
    if (!recipient) {
      if (recipientAddress !== null) {
        return { state: SwapCallbackState.INVALID, callback: null, error: 'Invalid recipient' }
      }
      return { state: SwapCallbackState.LOADING, callback: null, error: null }
    }
    if (!swapParams || !routerAddress) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Invalid swap parameters' }
    }

    return {
      state: SwapCallbackState.VALID,
      callback: async function onSwap(): Promise<string> {
        const { methodName, args, value } = swapParams

        // Convert value to bigint
        const valueAsBigInt = value && !isZero(value) ? ensureBigInt(value) : undefined

        try {
          // Execute the swap transaction using wagmi
          const hash = await writeContractAsync({
            address: routerAddress,
            abi: INomiswapRouter04 as any,
            functionName: methodName as any,
            args: args as any,
            value: valueAsBigInt,
            chainId,
          } as any)

          // Add transaction to state
          const inputSymbol = trade.inputAmount.currency.symbol
          const outputSymbol = trade.outputAmount.currency.symbol
          const inputAmount = trade.inputAmount.toSignificant(3)
          const outputAmount = trade.outputAmount.toSignificant(3)

          const base = `Swap ${inputAmount} ${inputSymbol} for ${outputAmount} ${outputSymbol}`
          const withRecipient =
            recipient === account
              ? base
              : `${base} to ${
                  recipientAddress && isAddress(recipientAddress) ? truncateHash(recipientAddress) : recipientAddress
                }`

          // Create a minimal transaction response object for compatibility
          const txResponse = { hash } as any

          addTransaction(txResponse, {
            summary: withRecipient,
          })

          return hash
        } catch (error: any) {
          // Handle user rejection
          if (error?.code === USER_DENIED_SIGNATURE || error?.code === 4001) {
            throw new Error('Transaction rejected.')
          }

          console.error(`Swap failed`, error, methodName, args, value)
          throw new Error(`Swap failed: ${error.message || error.shortMessage || 'Unknown error'}`)
        }
      },
      error: null,
    }
  }, [
    trade,
    account,
    chainId,
    recipient,
    recipientAddress,
    swapParams,
    routerAddress,
    writeContractAsync,
    addTransaction,
  ])
}
